import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { S, MANROPE, FOCUS, API } from "./lib";

const Label = ({ children }) => (
  <div className="text-[10px] font-bold uppercase tracking-[0.09em] mb-1" style={{ color: S.ink3 }}>{children}</div>
);
const inputCls = `w-full h-9 rounded-[7px] px-2.5 text-[13px] bg-white ${FOCUS}`;
const inputStyle = { border: `1px solid ${S.border}`, color: S.ink };
const Card = ({ title, children }) => (
  <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
    <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>{title}</div>
    {children}
  </div>
);

const LIST_FIELDS = [
  ["streams", "Streams", "One per line — the posting rhythm (Monday feature, Thursday industry…)"],
  ["verticals", "Verticals", "One per line — the industries you write for"],
  ["linkedin_accounts", "LinkedIn accounts", "One per line"],
  ["link_placement_options", "Link placement options", "One per line"],
];

const RULE_LISTS = [
  ["competitor_names", "Competitor names", "One per line — naming any of these blocks a post"],
  ["approved_customer_names", "Approved customer names", "One per line — customers cleared to be named, and therefore to carry a figure"],
  ["cta_phrases", "Approved calls to action", "One per line — LinkedIn copy closing with none of these is blocked. Patterns like \"book a … walkthrough\" are matched as well"],
  ["approved_demo_names", "Approved demo names", "One per line — the only company and operator names allowed in a design prompt"],
  ["oem_names", "Other people's brands", "One per line — machine brands, model codes and real third parties we keep out of our posts"],
  ["guardian_scheduling_words", "Guardian drift words", "One per line — scheduling language on a Guardian post is blocked"],
  ["invoicing_words", "Invoicing words", "One per line — used to spot invoicing posts bunched too close"],
];

const RULE_SENTENCES = {
  competitor_named: "A competitor is named in the copy",
  unattributed_figure: "A customer figure appears with nothing behind it",
  guardian_scheduling_drift: "A Guardian post strays into scheduling",
  missing_cta: "The LinkedIn copy never asks for anything",
  wrong_link_placement: "The link is not where it belongs for the channel",
  invoicing_too_close: "Two invoicing posts are running too close together",
  open_dependency: "A dependency is still in the way",
  no_channel_copy: "There is no copy on any channel",
  hashtag_overload: "More hashtags than the house limit",
  link_in_copy: "A link sits in the copy instead of the first comment",
  hook_too_long: "The hook is too long to survive the fold",
  copy_too_long: "The copy is over the channel limit",
  shouting: "Too many words in capitals",
  emoji_overload: "Too many emoji",
  no_creative_brief: "No creative brief yet",
  no_destination_link: "No destination link",
  no_vertical: "No vertical set",
  duplicate_hook: "The same hook is used on another post nearby",
  demo_names_only: "The design prompt names a company or person that is not on the approved demo list",
  linkedin_brand_hashtag: "LinkedIn does not have exactly three hashtags led by #EQUIPR",
  oem_or_third_party_name: "Someone else's brand or machine model is named",
};

export default function SocialSettings({ onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [staff, setStaff] = useState([]);
  const [codes, setCodes] = useState({ blocking: [], warning: [] });
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [campaign, setCampaign] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setS(r.data))
      .catch(() => toast.error("Could not load the marketing settings"));
    axios.get(`${API}/staff`).then((r) => setStaff(r.data.staff || [])).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/social/planner/rule-codes`)
      .then((r) => setCodes(r.data)).catch(() => {});
  }, []);

  const rules = s?.rules || {};
  const setRule = (k, v) => setS({ ...s, rules: { ...rules, [k]: v } });
  const toggleCode = (bucket, code) => {
    const list = rules[bucket] || [];
    setRule(bucket, list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);
  };

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setProduct = (i, k) => (e) => {
    const products = s.products.map((p, idx) => (idx === i ? { ...p, [k]: e.target.value } : p));
    setS({ ...s, products });
  };
  const addProduct = () => setS({
    ...s, products: [...s.products, { key: `product_${s.products.length + 1}`, label: "New product",
      colour: "#8A857A", landing_url: "", utm_slug: "", design_template: "" }],
  });
  const removeProduct = (i) => setS({ ...s, products: s.products.filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, s);
      toast.success("Marketing settings saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save those settings"); }
    finally { setBusy(false); }
  };

  const runImport = async (dryRun) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.info("Choose the spreadsheet first"); return; }
    setImporting(true); setResult(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const params = { dry_run: dryRun };
      if (campaign.trim()) params.campaign = campaign.trim();
      const r = await axios.post(`${API}/import`, body, { params });
      setResult(r.data);
      toast.success(dryRun
        ? `${r.data.created} rows read, ${r.data.skipped} skipped — nothing saved yet`
        : `${r.data.created} added, ${r.data.updated} updated`);
      if (!dryRun) {
        const fresh = await axios.get(`${API}/settings`);
        setS(fresh.data);
        onSaved?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "That import didn't work"); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex justify-end" data-testid="social-settings">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.32)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[860px] overflow-y-auto"
        style={{ background: S.panel, borderLeft: `1px solid ${S.border}` }}>
        <div className="sticky top-0 z-10 px-5 py-3.5 flex items-center gap-3"
          style={{ background: S.card, borderBottom: `1px solid ${S.border}` }}>
          <div>
            <div style={{ ...MANROPE, fontSize: 16, fontWeight: 800, color: S.ink }}>Marketing settings</div>
            <div className="text-[11.5px]" style={{ color: S.ink2 }}>
              Products, landing pages, UTMs and the lists behind every dropdown.
            </div>
          </div>
          <button onClick={save} disabled={busy || !s} data-testid="social-settings-save"
            className={`ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase disabled:opacity-60 ${FOCUS}`}
            style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000" }}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} data-testid="social-settings-close"
            className={`w-9 h-9 rounded-[7px] flex items-center justify-center ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, background: S.card }}><X className="w-4 h-4" /></button>
        </div>

        {!s ? (
          <div className="px-5 py-10 text-[13px]" style={{ color: S.ink2 }}>Loading…</div>
        ) : (
          <div className="px-5 py-4 space-y-3.5">
            <Card title="Links and tracking">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Root domain</Label>
                  <input value={s.root_domain || ""} onChange={set("root_domain")} className={inputCls}
                    style={inputStyle} data-testid="social-settings-root" /></div>
                <div><Label>utm_medium</Label>
                  <input value={s.utm_medium || ""} onChange={set("utm_medium")} className={inputCls}
                    style={inputStyle} data-testid="social-settings-medium" /></div>
                <div><Label>utm_source — LinkedIn</Label>
                  <input value={s.utm_source_linkedin || ""} onChange={set("utm_source_linkedin")}
                    className={inputCls} style={inputStyle} data-testid="social-settings-source-li" /></div>
                <div><Label>utm_source — Facebook/Instagram</Label>
                  <input value={s.utm_source_meta || ""} onChange={set("utm_source_meta")}
                    className={inputCls} style={inputStyle} data-testid="social-settings-source-meta" /></div>
                <div><Label>Default owner — who new posts are nudged to</Label>
                  <select value={s.default_owner_id || ""} data-testid="social-settings-owner"
                    onChange={(e) => {
                      const person = staff.find((x) => x.id === e.target.value);
                      setS({ ...s, default_owner_id: person ? person.id : null,
                        default_owner_name: person ? person.name : null });
                    }}
                    className={`w-full h-9 rounded-[7px] px-2 text-[13px] bg-white ${FOCUS}`}
                    style={inputStyle}>
                    <option value="">Nobody</option>
                    {staff.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select></div>
              </div>
            </Card>

            <Card title="Products">
              {(s.products || []).map((p, i) => (
                <div key={p.key} className="rounded-[8px] p-3 space-y-2.5"
                  style={{ background: S.panel, border: `1px solid ${S.divider}`, borderLeft: `3px solid ${p.colour || S.muted}` }}
                  data-testid={`social-settings-product-${p.key}`}>
                  <div className="grid sm:grid-cols-4 gap-2.5">
                    <div><Label>Label</Label><input value={p.label || ""} onChange={setProduct(i, "label")}
                      className={inputCls} style={inputStyle} /></div>
                    <div><Label>Landing URL</Label><input value={p.landing_url || ""} onChange={setProduct(i, "landing_url")}
                      className={inputCls} style={inputStyle} /></div>
                    <div><Label>utm_content slug</Label><input value={p.utm_slug || ""} onChange={setProduct(i, "utm_slug")}
                      className={inputCls} style={inputStyle} /></div>
                    <div><Label>Colour</Label>
                      <div className="flex items-center gap-2">
                        <input value={p.colour || ""} onChange={setProduct(i, "colour")} className={inputCls} style={inputStyle} />
                        <button onClick={() => removeProduct(i)} title="Remove"
                          className={`w-9 h-9 shrink-0 rounded-[7px] flex items-center justify-center ${FOCUS}`}
                          style={{ border: "1px solid #E8C4C4", color: "#8E1212", background: S.card }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div><Label>Claude design base template</Label>
                    <textarea value={p.design_template || ""} onChange={setProduct(i, "design_template")} rows={3}
                      className={`w-full rounded-[7px] px-2.5 py-2 text-[12.5px] bg-white ${FOCUS}`}
                      style={{ ...inputStyle, lineHeight: 1.5 }} /></div>
                </div>
              ))}
              <button onClick={addProduct} data-testid="social-settings-add-product"
                className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase inline-flex items-center gap-1.5 ${FOCUS}`}
                style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2 }}>
                <Plus className="w-3.5 h-3.5" /> Add product
              </button>
            </Card>

            <Card title="Dropdown lists">
              <div className="grid sm:grid-cols-2 gap-3">
                {LIST_FIELDS.map(([k, label, hint]) => (
                  <div key={k}>
                    <Label>{label}</Label>
                    <textarea value={(s[k] || []).join("\n")} rows={5} data-testid={`social-settings-${k}`}
                      onChange={(e) => setS({ ...s, [k]: e.target.value.split("\n").map((v) => v.trim()).filter(Boolean) })}
                      className={`w-full rounded-[7px] px-2.5 py-2 text-[12.5px] bg-white ${FOCUS}`}
                      style={{ ...inputStyle, lineHeight: 1.5 }} />
                    <p className="text-[11px] mt-1" style={{ color: S.ink3 }}>{hint}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="House rules — the word lists">
              <p className="text-[12.5px]" style={{ color: S.ink2 }}>
                These run on every change to a post, whoever or whatever made it. A <b>blocking</b>{" "}
                finding stops a post going for approval until it is fixed; a <b>warning</b> is
                recorded and shown beside the post but holds nothing up.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {RULE_LISTS.map(([k, label, hint]) => (
                  <div key={k}>
                    <Label>{label}</Label>
                    <textarea value={(rules[k] || []).join("\n")} rows={5} data-testid={`social-rules-${k}`}
                      onChange={(e) => setRule(k, e.target.value.split("\n").map((v) => v.trim()).filter(Boolean))}
                      className={`w-full rounded-[7px] px-2.5 py-2 text-[12.5px] bg-white ${FOCUS}`}
                      style={{ ...inputStyle, lineHeight: 1.5 }} />
                    <p className="text-[11px] mt-1" style={{ color: S.ink3 }}>{hint}</p>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-4 gap-2.5">
                <div><Label>Days between invoicing posts</Label>
                  <input type="number" value={rules.invoicing_min_gap_days ?? 10}
                    onChange={(e) => setRule("invoicing_min_gap_days", Number(e.target.value))}
                    className={inputCls} style={inputStyle} data-testid="social-rules-gap" /></div>
                <div><Label>Hook character limit</Label>
                  <input type="number" value={rules.hook_max_chars ?? 140}
                    onChange={(e) => setRule("hook_max_chars", Number(e.target.value))}
                    className={inputCls} style={inputStyle} data-testid="social-rules-hook" /></div>
                <div><Label>Emoji limit</Label>
                  <input type="number" value={rules.emoji_max ?? 4}
                    onChange={(e) => setRule("emoji_max", Number(e.target.value))}
                    className={inputCls} style={inputStyle} data-testid="social-rules-emoji" /></div>
                <div><Label>LinkedIn hashtags wanted</Label>
                  <input type="number" value={rules.linkedin_hashtag_count ?? 3}
                    onChange={(e) => setRule("linkedin_hashtag_count", Number(e.target.value))}
                    className={inputCls} style={inputStyle} data-testid="social-rules-brandcount" /></div>
                <div><Label>Lead hashtag</Label>
                  <input value={rules.brand_hashtag ?? "#EQUIPR"}
                    onChange={(e) => setRule("brand_hashtag", e.target.value)}
                    className={inputCls} style={inputStyle} data-testid="social-rules-brandtag" /></div>
                <div><Label>LinkedIn hashtag limit</Label>
                  <input type="number" value={(rules.max_hashtags || {}).linkedin ?? 3}
                    onChange={(e) => setRule("max_hashtags",
                      { ...(rules.max_hashtags || {}), linkedin: Number(e.target.value) })}
                    className={inputCls} style={inputStyle} data-testid="social-rules-hashtags" /></div>
              </div>
            </Card>

            <Card title="House rules — what each one does">
              <div className="space-y-1.5">
                {[...(codes.blocking || []), ...(codes.warning || [])].map((code) => {
                  const muted = (rules.muted || []).includes(code);
                  const soft = (rules.warn_only || []).includes(code);
                  const isBlocking = (codes.blocking || []).includes(code);
                  return (
                    <div key={code} className="flex items-center gap-2 text-[12.5px] py-1"
                      style={{ borderBottom: `1px solid ${S.divider}` }} data-testid={`social-rule-${code}`}>
                      <span className="flex-1" style={{ color: muted ? S.muted : S.ink }}>
                        {RULE_SENTENCES[code] || code}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] w-[70px]"
                        style={{ color: muted ? S.muted : (isBlocking && !soft) ? S.bad : S.accentInk }}>
                        {muted ? "off" : (isBlocking && !soft) ? "blocks" : "warns"}
                      </span>
                      {isBlocking && (
                        <label className="flex items-center gap-1 text-[11.5px]" style={{ color: S.ink2 }}>
                          <input type="checkbox" checked={soft} data-testid={`social-rule-soft-${code}`}
                            onChange={() => toggleCode("warn_only", code)} /> warn only
                        </label>
                      )}
                      <label className="flex items-center gap-1 text-[11.5px]" style={{ color: S.ink2 }}>
                        <input type="checkbox" checked={muted} data-testid={`social-rule-mute-${code}`}
                          onChange={() => toggleCode("muted", code)} /> off
                      </label>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="Import the Social Master Calendar">
              <p className="text-[12.5px]" style={{ color: S.ink2 }}>
                Reads the <b>Master</b> tab, keyed on Post ID, so re-running it updates rather than duplicates.
                If the workbook carries Settings and Design Templates tabs, the landing pages, UTM slugs
                and base prompts come across with it.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Spreadsheet (.xlsx or .csv)</Label>
                  <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" data-testid="social-import-file"
                    className="text-[12.5px] w-full" /></div>
                <div><Label>Tag everything with a campaign (optional)</Label>
                  <input value={campaign} onChange={(e) => setCampaign(e.target.value)} className={inputCls}
                    style={inputStyle} placeholder="e.g. CICA 2026" data-testid="social-import-campaign" /></div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => runImport(true)} disabled={importing} data-testid="social-import-dry"
                  className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase disabled:opacity-60 ${FOCUS}`}
                  style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2 }}>
                  Check it first
                </button>
                <button onClick={() => runImport(false)} disabled={importing} data-testid="social-import-run"
                  className={`h-9 px-3.5 rounded-[7px] text-[11.5px] font-bold uppercase inline-flex items-center gap-1.5 disabled:opacity-60 ${FOCUS}`}
                  style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000" }}>
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import
                </button>
              </div>
              {result && (
                <div className="rounded-[7px] px-3 py-2.5 text-[12px]" data-testid="social-import-result"
                  style={{ background: S.panel, color: S.ink2 }}>
                  {result.dry_run ? "Dry run — nothing saved. " : ""}
                  {result.rows} rows read · {result.created} {result.dry_run ? "would import" : "added"} ·{" "}
                  {result.updated} updated · {result.skipped} skipped
                  {result.problems?.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5" style={{ color: S.bad }}>
                      {result.problems.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
